import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './HarnettAcquisitionPanel.css';

const SOURCE_ID = 'nc_harnett_sheriff';
const ACTIVE = new Set(['QUEUED', 'RUNNING', 'RETRY_BACKOFF', 'PAUSE_REQUESTED', 'STOP_REQUESTED']);
const formatTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};
const identityName = (identity = {}) => [identity.firstName, identity.middleName,
  identity.lastName, identity.suffix].filter(Boolean).join(' ') || 'Unnamed person';
const metricValue = (value) => Number.isSafeInteger(value) && value >= 0 ? value : 'N/A';
const summedMetricValue = (...values) => values.every((value) =>
  Number.isSafeInteger(value) && value >= 0) ? values.reduce((sum, value) => sum + value, 0) : 'N/A';

const HarnettAcquisitionPanel = ({ functionsInstance = functions, initiallyOpen = false }) => {
  const [open, setOpen] = useState(initiallyOpen);
  const [status, setStatus] = useState(null);
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ startDate: '', endDate: '', batchSize: '20',
    maxTotalRecords: '' });
  const [catchUpThroughDate, setCatchUpThroughDate] = useState('');
  const [reviews, setReviews] = useState([]);

  const control = useCallback((payload) =>
    httpsCallable(functionsInstance, 'publicRecordHarnettControl')({
      sourceId: SOURCE_ID,
      ...payload,
    }), [functionsInstance]);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setBusy('refresh');
    setMessage('');
    try {
      const response = await control({ action: 'status' });
      setStatus(response?.data || null);
      setAvailable(true);
    } catch (error) {
      setAvailable(false);
      setStatus(null);
      const code = String(error?.code || '').replace(/^functions\//, '');
      setMessage(code === 'not-found' || code === 'unimplemented' ?
        'Harnett control service is not registered. Controls remain disabled until the isolated backend export is approved.' :
        'Harnett control status is unavailable. No command was sent.');
    } finally {
      if (!quiet) setBusy('');
    }
  }, [control]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const runAction = async (action, payload = {}) => {
    if (!available || busy) return;
    setBusy(action);
    setMessage('');
    try {
      await control({ action, ...payload });
      await refresh({ quiet: true });
    } catch (error) {
      setMessage(error?.message || 'The Harnett command failed without changing other counties.');
    } finally {
      setBusy('');
    }
  };

  const start = (event) => {
    event.preventDefault();
    const batchSize = Number(form.batchSize);
    const maxTotalRecords = form.maxTotalRecords === '' ? null : Number(form.maxTotalRecords);
    if (!form.startDate || !form.endDate || form.startDate > form.endDate ||
      !Number.isInteger(batchSize) || batchSize < 1 || batchSize > 100 ||
      (maxTotalRecords !== null && (!Number.isInteger(maxTotalRecords) ||
        maxTotalRecords < 1 || maxTotalRecords > 100))) {
      setMessage('Enter a valid date range, a batch size from 1 to 100, and an optional total limit from 1 to 100.');
      return;
    }
    runAction('start', { startDate: form.startDate, endDate: form.endDate, batchSize,
      maxTotalRecords });
  };

  const loadReviews = async () => {
    if (!available || busy) return;
    setBusy('reviews');
    try {
      const response = await control({ action: 'reviews', limit: 50 });
      setReviews(response?.data?.reviews || []);
    } catch (error) {
      setMessage(error?.message || 'Harnett identity reviews are unavailable.');
    } finally {
      setBusy('');
    }
  };

  const decideReview = async (review, decision, targetSourceIdentityId = '') => {
    await runAction('review_decide', { reviewId: review.reviewId, decision,
      targetSourceIdentityId });
    await loadReviews();
  };

  const job = status?.job || null;
  const worker = status?.worker || { connected: false, chromeOpen: false };
  const workerLabel = !open ? 'Not checked' : !available ? 'Control unavailable' : worker.connected ?
    (worker.chromeOpen ? 'Connected - Chrome open' : 'Connected - idle') : 'Disconnected';
  const active = ACTIVE.has(job?.status);
  const paused = job?.status === 'PAUSED';
  const startEnabled = available && worker.connected && !active && !busy;
  const counters = job?.counters || {};

  return <section className="harnett-panel" aria-labelledby="harnett-panel-heading">
    <div className="harnett-panel-header">
      <div>
        <p className="harnett-eyebrow">Independent local worker</p>
        <h3 id="harnett-panel-heading">Harnett County</h3>
      </div>
      <div className={`harnett-worker-state ${open && worker.connected ? 'is-connected' : 'is-disconnected'}`}>
        <span aria-hidden="true" />{workerLabel}
      </div>
    </div>
    <p className="harnett-panel-note">Manual, source-scoped acquisition through visible Chrome. No automatic schedule.</p>
    <button type="button" className="harnett-panel-toggle" onClick={() => setOpen((value) => !value)}>
      {open ? 'Close Harnett controls' : 'Open Harnett controls'}
    </button>

    {open && <>
    <dl className="harnett-status-grid">
      <div><dt>Current acquisition state</dt><dd>{job?.status || (available ? 'IDLE' : 'N/A')}</dd></div>
      <div><dt>Requested range</dt><dd>{job ? `${job.startDate} - ${job.endDate}` : 'N/A'}</dd></div>
      <div><dt>Current partition</dt><dd aria-label="Current partition value">{job?.currentWindowIndex !== undefined ?
        `Window ${job.currentWindowIndex + 1}` : 'N/A'}</dd></div>
      <div><dt>Current page</dt><dd>{job?.currentPage || 'N/A'}</dd></div>
      <div><dt>Records discovered</dt><dd aria-label="Records discovered value">{metricValue(counters.recordsDiscovered)}</dd></div>
      <div><dt>Records normalized</dt><dd aria-label="Records normalized value">{metricValue(counters.recordsProcessed)}</dd></div>
      <div><dt>Total record attempts</dt><dd aria-label="Total record attempts value">{job ?
        `${metricValue(job.totalRecordsAttempted)} / ${job.maxTotalRecords ?? 'Unbounded'}` : 'N/A'}</dd></div>
      <div><dt>Records skipped</dt><dd aria-label="Records skipped value">{summedMetricValue(counters.invalidRecordsSkipped,
        counters.unverifiableDateRecords)}</dd></div>
      <div><dt>Out-of-window records accepted</dt><dd aria-label="Out-of-window records accepted value">{
        metricValue(counters.outOfRangeRecordsAccepted)}</dd></div>
      <div><dt>Records persisted</dt><dd aria-label="Records persisted value">{metricValue(counters.recordsRetained)}</dd></div>
      <div><dt>Duplicate records</dt><dd aria-label="Duplicate records value">{metricValue(counters.duplicateBookingsSkipped)}</dd></div>
      <div><dt>Source identities</dt><dd aria-label="Source identities value">N/A</dd></div>
      <div><dt>Identity reuse events</dt><dd>N/A</dd></div>
      <div><dt>Photo attempts</dt><dd aria-label="Photo attempts value">N/A</dd></div>
      <div><dt>Photos downloaded</dt><dd>N/A</dd></div>
      <div><dt>Photo failures</dt><dd>{metricValue(counters.photosMissing)}</dd></div>
      <div><dt>Retained photographs</dt><dd aria-label="Retained photographs value">N/A</dd></div>
      <div><dt>Indexed faces</dt><dd aria-label="Indexed faces value">Not activated</dd></div>
      <div><dt>Pending manual reviews</dt><dd aria-label="Pending manual reviews value">{metricValue(counters.pendingIdentityReview)}</dd></div>
      <div><dt>Failed acquisition windows</dt><dd>{job ? metricValue(job.failedWindows?.length) : 'N/A'}</dd></div>
      <div><dt>Acquisition checkpoint</dt><dd aria-label="Acquisition checkpoint value">{job?.lastSuccessfulCheckpoint || 'N/A'}</dd></div>
      <div><dt>Last activity</dt><dd>{formatTime(job?.updatedAt)}</dd></div>
      <div><dt>Last completed</dt><dd>{formatTime(job?.lastCompletedAt)}</dd></div>
      <div><dt>Worker heartbeat</dt><dd>{formatTime(worker.lastHeartbeatAt)}</dd></div>
    </dl>

    {job?.failedWindows?.length > 0 && <details className="harnett-diagnostics">
      <summary>Failed windows and diagnostics</summary>
      <ul>{job.failedWindows.map((failure) => <li key={`${failure.startDate}-${failure.failedAt}`}>
        {failure.startDate} - {failure.endDate}: {failure.errorCode} ({failure.status})
      </li>)}</ul>
    </details>}

    <div className="harnett-actions" aria-label="Harnett acquisition controls">
      <button type="button" disabled={!available || !active || busy === 'pause'}
        onClick={() => runAction('pause')}>Pause</button>
      <button type="button" disabled={!available || job?.status !== 'PAUSED' || busy === 'resume'}
        onClick={() => runAction('resume')}>Resume</button>
      <button type="button" className="harnett-stop" disabled={!available || (!active && !paused) || busy === 'stop'}
        onClick={() => runAction('stop')}>Stop</button>
      <button type="button" disabled={Boolean(busy)} onClick={() => refresh()}>Refresh status</button>
    </div>

    <form className="harnett-start-form" onSubmit={start}>
      <h4>Historical acquisition</h4>
      <label>Start date<input type="date" value={form.startDate}
        onChange={(event) => setForm({ ...form, startDate: event.target.value })} /></label>
      <label>End date<input type="date" value={form.endDate}
        onChange={(event) => setForm({ ...form, endDate: event.target.value })} /></label>
      <label>Records per processing batch<input type="number" min="1" max="100" value={form.batchSize}
        onChange={(event) => setForm({ ...form, batchSize: event.target.value })} /></label>
      <label>Total record-attempt limit (optional)<input type="number" min="1" max="100"
        value={form.maxTotalRecords}
        onChange={(event) => setForm({ ...form, maxTotalRecords: event.target.value })} /></label>
      <p>Leave the total limit blank for an unbounded acquisition. Excluded dates do not consume it.</p>
      <button type="submit" disabled={!startEnabled}>Start acquisition</button>
      {!worker.connected && available && <p>Start is disabled until the local Harnett worker is connected.</p>}
    </form>

    <div className="harnett-catch-up">
      <h4>Catch-up from checkpoint</h4>
      <label>Through date<input type="date" value={catchUpThroughDate}
        onChange={(event) => setCatchUpThroughDate(event.target.value)} /></label>
      <button type="button" disabled={!startEnabled || !job?.lastSuccessfulCheckpoint || !catchUpThroughDate}
        onClick={() => runAction('catch_up', { throughDate: catchUpThroughDate,
          batchSize: Number(form.batchSize), maxTotalRecords: form.maxTotalRecords === '' ? null :
            Number(form.maxTotalRecords) })}>Start catch-up</button>
    </div>

    <div className="harnett-reviews">
      <div className="harnett-review-heading"><h4>Manual identity reviews</h4>
        <button type="button" disabled={!available || Boolean(busy)} onClick={loadReviews}>View reviews</button></div>
      {reviews.map((review) => <article key={review.reviewId} className="harnett-review-card">
        <p><strong>{identityName(review.incomingIdentity)}</strong></p>
        <p>{review.reasonCode.replaceAll('_', ' ').toLowerCase()}</p>
        {review.candidates.map((candidate) => <div key={candidate.sourceIdentityId}
          className="harnett-review-candidate">
          <span>{identityName(candidate.identity)}</span>
          <button type="button" disabled={Boolean(busy)}
            onClick={() => decideReview(review, 'DUPLICATE', candidate.sourceIdentityId)}>Duplicate</button>
        </div>)}
        <button type="button" disabled={Boolean(busy)}
          onClick={() => decideReview(review, 'NEW_PERSON')}>New Person</button>
      </article>)}
    </div>
    {message && <p className="harnett-message" role={available ? 'status' : 'alert'}>{message}</p>}
    </>}
  </section>;
};

export default HarnettAcquisitionPanel;
